import { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import WinShareCard from '../components/WinShareCard';

const API  = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const fnGrad = 'linear-gradient(270deg,#1DBEFF,#9B4DCA,#FFDE00)';

function formatUSDC(raw) {
  if (!raw) return '$0.00';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function MyProfile({ walletAddress }) {
  const [apexId, setApexId]             = useState('');
  const [input, setInput]               = useState('');
  const [data, setData]                 = useState(null);
  const [stats, setStats]               = useState(null);
  const [statsError, setStatsError]     = useState(null);
  const [referrals, setReferrals]       = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [regError, setRegError]         = useState(null);
  const [regOk, setRegOk]               = useState(false);
  const [copied, setCopied]             = useState(false);
  const [shareWin, setShareWin]         = useState(null);
  // Fortnite state
  const [epicInput, setEpicInput]       = useState('');
  const [epicId, setEpicId]             = useState('');
  const [fnData, setFnData]             = useState(null);
  const [fnStats, setFnStats]           = useState(null);
  const [fnLoading, setFnLoading]       = useState(false);
  const [fnError, setFnError]           = useState(null);
  const [fnRegError, setFnRegError]     = useState(null);
  const [fnRegOk, setFnRegOk]           = useState(false);
  // Tarkov state
  const [tarkovInput, setTarkovInput]   = useState('');
  const [tarkovId, setTarkovId]         = useState('');
  const [tarkovData, setTarkovData]     = useState(null);
  const [tarkovStats, setTarkovStats]   = useState(null);
  const [tarkovLoading, setTarkovLoading] = useState(false);
  const [tarkovError, setTarkovError]   = useState(null);
  const [tarkovRegError, setTarkovRegError] = useState(null);
  const [tarkovRegOk, setTarkovRegOk]   = useState(false);
  // Display name + bio
  const [displayName, setDisplayName]   = useState(() => localStorage.getItem('displayName') || '');
  const [bio, setBio]                   = useState(() => localStorage.getItem('bio') || '');
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({ displayName: '', bio: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg]     = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('apexPlayerId');
    if (saved) { setApexId(saved); fetchProfile(saved); }
    const savedEpic = localStorage.getItem('epicPlayerId');
    if (savedEpic) { setEpicId(savedEpic); fetchFortniteProfile(savedEpic); }
    const savedTarkov = localStorage.getItem('tarkovPlayerId');
    if (savedTarkov) { setTarkovId(savedTarkov); fetchTarkovProfile(savedTarkov); }
  }, []);

  async function fetchProfile(id) {
    setLoading(true); setError(null);
    try {
      const { data: d } = await axios.get(`${API}/user/${encodeURIComponent(id)}`);
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || 'Player not found');
    } finally { setLoading(false); }
    // Fetch live Tracker.gg stats + referrals in parallel
    setStatsLoading(true);
    const [statsRes, refRes] = await Promise.allSettled([
      axios.get(`${API}/user/${encodeURIComponent(id)}/stats`),
      axios.get(`${API}/user/${encodeURIComponent(id)}/referrals`),
    ]);
    if (statsRes.status === 'fulfilled') {
      setStats(statsRes.value.data.stats);
      setStatsError(null);
    } else {
      setStats(null);
      const msg = statsRes.reason?.response?.data?.error || statsRes.reason?.message || 'Unknown error';
      setStatsError(msg);
    }
    setReferrals(refRes.status === 'fulfilled' ? refRes.value.data : null);
    setStatsLoading(false);
  }

  async function fetchFortniteProfile(id) {
    setFnLoading(true); setFnError(null);
    try {
      const [profileRes, statsRes] = await Promise.allSettled([
        axios.get(`${API}/user/fortnite/${encodeURIComponent(id)}`),
        axios.get(`${API}/user/fortnite/${encodeURIComponent(id)}/stats`),
      ]);
      if (profileRes.status === 'fulfilled') setFnData(profileRes.value.data);
      else setFnError(profileRes.reason?.response?.data?.error || 'Profile not found');
      setFnStats(statsRes.status === 'fulfilled' ? statsRes.value.data.stats : null);
    } finally { setFnLoading(false); }
  }

  async function fetchTarkovProfile(id) {
    setTarkovLoading(true); setTarkovError(null);
    try {
      const [profileRes, statsRes] = await Promise.allSettled([
        axios.get(`${API}/user/tarkov/${encodeURIComponent(id)}`),
        axios.get(`${API}/user/tarkov/${encodeURIComponent(id)}/stats`),
      ]);
      if (profileRes.status === 'fulfilled') setTarkovData(profileRes.value.data);
      else setTarkovError(profileRes.reason?.response?.data?.error || 'Profile not found');
      setTarkovStats(statsRes.status === 'fulfilled' ? statsRes.value.data.stats : null);
    } finally { setTarkovLoading(false); }
  }

  async function handleTarkovRegister(e) {
    e.preventDefault();
    if (!tarkovInput.trim()) return;
    if (!walletAddress) { setTarkovRegError('Connect your wallet first'); return; }
    setTarkovRegError(null); setTarkovRegOk(false);
    try {
      await axios.post(`${API}/user/tarkov/register`, { tarkovPlayerId: tarkovInput.trim(), walletAddress });
      localStorage.setItem('tarkovPlayerId', tarkovInput.trim());
      setTarkovId(tarkovInput.trim());
      setTarkovRegOk(true);
      fetchTarkovProfile(tarkovInput.trim());
    } catch (e) {
      const msg = e.response?.data?.error || 'Registration failed';
      if (msg.includes('already registered') || msg.includes('already has')) {
        localStorage.setItem('tarkovPlayerId', tarkovInput.trim());
        setTarkovId(tarkovInput.trim());
        fetchTarkovProfile(tarkovInput.trim());
      } else { setTarkovRegError(msg); }
    }
  }

  async function handleFnRegister(e) {
    e.preventDefault();
    if (!epicInput.trim()) return;
    if (!walletAddress) { setFnRegError('Connect your wallet first'); return; }
    setFnRegError(null); setFnRegOk(false);
    try {
      await axios.post(`${API}/user/fortnite/register`, { epicPlayerId: epicInput.trim(), walletAddress });
      localStorage.setItem('epicPlayerId', epicInput.trim());
      setEpicId(epicInput.trim());
      setFnRegOk(true);
      fetchFortniteProfile(epicInput.trim());
    } catch (e) {
      const msg = e.response?.data?.error || 'Registration failed';
      if (msg.includes('already registered') || msg.includes('already has')) {
        localStorage.setItem('epicPlayerId', epicInput.trim());
        setEpicId(epicInput.trim());
        fetchFortniteProfile(epicInput.trim());
      } else { setFnRegError(msg); }
    }
  }

  function copyReferralLink(code) {
    navigator.clipboard.writeText(`https://www.ezegames.live/lobby?ref=${code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (!input.trim()) return;
    if (!walletAddress) { setRegError('Connect your wallet first'); return; }
    setRegError(null); setRegOk(false);
    try {
      await axios.post(`${API}/user/register`, { apexPlayerId: input.trim(), walletAddress });
      localStorage.setItem('apexPlayerId', input.trim());
      setApexId(input.trim());
      setRegOk(true);
      fetchProfile(input.trim());
    } catch (e) {
      const msg = e.response?.data?.error || 'Registration failed';
      if (msg.includes('already registered')) {
        localStorage.setItem('apexPlayerId', input.trim());
        setApexId(input.trim());
        fetchProfile(input.trim());
      } else { setRegError(msg); }
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    if (!walletAddress) { setProfileMsg('Connect your wallet first.'); return; }
    setProfileSaving(true); setProfileMsg('');
    try {
      const { data: res } = await axios.patch(`${API}/user/profile`, {
        walletAddress,
        displayName: profileDraft.displayName,
        bio:         profileDraft.bio,
      });
      const dn = res.user.display_name || '';
      const b  = res.user.bio || '';
      setDisplayName(dn);
      setBio(b);
      localStorage.setItem('displayName', dn);
      localStorage.setItem('bio', b);
      setProfileMsg('Saved!');
      setEditingProfile(false);
    } catch (err) {
      setProfileMsg(err.response?.data?.error || 'Failed to save');
    } finally {
      setProfileSaving(false);
    }
  }

  const s = {
    page:      { minHeight: '100vh', background: 'var(--bg-primary)', padding: '40px 24px', maxWidth: 700, margin: '0 auto' },
    title:     { fontSize: 28, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#fff', marginBottom: 32 },
    card:      { background: 'var(--bg-card)', border: '1px solid #00D4FF22', borderRadius: 14, padding: 24, boxShadow: '0 0 15px #00D4FF33', marginBottom: 20 },
    label:     { fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8, display: 'block' },
    input:     { width: '100%', background: '#ffffff08', border: '1px solid #00D4FF44', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#fff', outline: 'none', marginBottom: 10 },
    btn:       { width: '100%', padding: '12px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', letterSpacing: 0.8, background: 'linear-gradient(270deg,#00D4FF,#8B00FF,#FF006E)', backgroundSize: '400% 400%', animation: 'gradientShift 4s ease infinite' },
    stat:      { textAlign: 'center', flex: 1 },
    statVal:   { fontSize: 28, fontWeight: 800, background: 'linear-gradient(270deg,#00D4FF,#8B00FF,#FF006E)', backgroundSize: '400% 400%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'gradientShift 4s ease infinite' },
    statLbl:   { fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-secondary)' },
    row:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #ffffff08', fontSize: 13 },
    error:     { fontSize: 12, color: '#FF006E', marginBottom: 8 },
    success:   { fontSize: 12, color: '#00D4FF', marginBottom: 8 },
  };

  return (
    <div style={s.page}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>My Profile | EZGAME</title>
      </Helmet>
      <h1 style={s.title}>My <span style={{ background: 'linear-gradient(270deg,#00D4FF,#8B00FF,#FF006E)', backgroundSize: '400% 400%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'gradientShift 4s ease infinite' }}>Profile</span></h1>

      {/* ── Display name + bio card ── */}
      <div style={{ ...s.card, marginBottom: 36 }}>
        {!editingProfile ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>
                  {displayName || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: 16, fontWeight: 400 }}>No display name set</span>}
                </div>
                {bio
                  ? <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{bio}</div>
                  : <div style={{ fontSize: 12, color: '#ffffff22', fontStyle: 'italic' }}>No bio yet</div>
                }
              </div>
              <button
                onClick={() => { setProfileDraft({ displayName: displayName || '', bio: bio || '' }); setEditingProfile(true); setProfileMsg(''); }}
                style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', border: '1px solid #00D4FF44', borderRadius: 6, background: 'transparent', color: '#00D4FF', cursor: 'pointer', flexShrink: 0 }}
              >
                Edit
              </button>
            </div>
            {profileMsg && <div style={{ ...s.success, marginTop: 8 }}>{profileMsg}</div>}
          </div>
        ) : (
          <form onSubmit={handleSaveProfile}>
            <div style={{ marginBottom: 12 }}>
              <span style={s.label}>Display Name <span style={{ color: '#ffffff33', fontWeight: 400 }}>· max 25 chars</span></span>
              <input
                style={s.input}
                placeholder="Your tag (e.g. ShadowKing)"
                value={profileDraft.displayName}
                maxLength={25}
                onChange={e => setProfileDraft(d => ({ ...d, displayName: e.target.value }))}
                autoFocus
              />
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right', marginTop: -6, marginBottom: 12 }}>
                {profileDraft.displayName.length}/25
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={s.label}>Bio <span style={{ color: '#ffffff33', fontWeight: 400 }}>· max 50 chars</span></span>
              <input
                style={s.input}
                placeholder="e.g. Ranked Apex grinder · Top 5% KD"
                value={profileDraft.bio}
                maxLength={50}
                onChange={e => setProfileDraft(d => ({ ...d, bio: e.target.value }))}
              />
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right', marginTop: -6, marginBottom: 4 }}>
                {profileDraft.bio.length}/50
              </div>
            </div>
            {profileMsg && <div style={profileMsg === 'Saved!' ? s.success : s.error}>{profileMsg}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setEditingProfile(false)}
                style={{ flex: 1, padding: '10px', border: '1px solid #ffffff22', borderRadius: 8, background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={profileSaving}
                style={{ ...s.btn, flex: 2 }}>
                {profileSaving ? 'Saving…' : 'Save Profile'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* ── Apex section header ── */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#00D4FF', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'inline-block', width: 3, height: 14, background: '#00D4FF', borderRadius: 2 }} />
        Apex Profile
      </div>

      {!apexId && (
        <div style={s.card}>
          <span style={s.label}>Link your EA / Origin Username</span>
          <form onSubmit={handleRegister}>
            <input style={s.input} placeholder="Your EA / Origin Username (e.g. Shroud)" value={input} onChange={e => setInput(e.target.value)} />
            {regError && <div style={s.error}>{regError}</div>}
            {regOk    && <div style={s.success}>Registered!</div>}
            <button style={s.btn} type="submit">Save Profile</button>
          </form>
          {!walletAddress && <div style={{ ...s.error, marginTop: 8 }}>Connect your wallet in the top right first.</div>}
        </div>
      )}

      {apexId && !loading && !error && data && (
        <>
          <div style={s.card}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{data.user.apex_player_id}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{walletAddress || data.user.wallet_address}</div>
            <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
              {[
                { val: data.tournaments.length,                               lbl: 'Played'   },
                { val: data.tournaments.filter(t => t.won).length,           lbl: 'Won'       },
                { val: data.tournaments.length ? `${Math.round((data.tournaments.filter(t => t.won).length / data.tournaments.length) * 100)}%` : '0%', lbl: 'Win Rate' },
              ].map(({ val, lbl }) => (
                <div key={lbl} style={s.stat}>
                  <div style={s.statVal}>{val}</div>
                  <div style={s.statLbl}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Live Tracker.gg stats */}
          <div style={s.card}>
            <span style={s.label}>Apex Legends Stats <span style={{ color: '#00D4FF55', fontWeight: 400 }}>· live via Tracker.gg</span></span>
            {statsLoading && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Fetching live stats…</div>}
            {!statsLoading && !stats && (
              <div style={{ color: '#FF006E', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-word' }}>
                {statsError || 'Stats unavailable'}
              </div>
            )}
            {!statsLoading && stats && (
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                {[
                  { val: stats.kdRatio?.toFixed(2) ?? '—', lbl: 'K/D Ratio' },
                  { val: stats.kills?.toLocaleString() ?? '—', lbl: 'Total Kills' },
                  { val: stats.wins?.toLocaleString()  ?? '—', lbl: 'Wins' },
                ].map(({ val, lbl }) => (
                  <div key={lbl} style={s.stat}>
                    <div style={s.statVal}>{val}</div>
                    <div style={s.statLbl}>{lbl}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Referral card */}
          {data.user.referral_code && (
            <div style={s.card}>
              <span style={s.label}>Referral Program <span style={{ color: '#00D4FF55', fontWeight: 400 }}>· earn 5% per referred entry</span></span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <div style={{ flex: 1, background: '#ffffff08', border: '1px solid #00D4FF33', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#00D4FF', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ezegames.live/lobby?ref={data.user.referral_code}
                </div>
                <button onClick={() => copyReferralLink(data.user.referral_code)} style={{ ...s.btn, width: 'auto', padding: '10px 16px', fontSize: 12, flexShrink: 0 }}>
                  {copied ? '✓ Copied!' : 'Copy Link'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 24 }}>
                <div style={s.stat}>
                  <div style={s.statVal}>{referrals?.count ?? 0}</div>
                  <div style={s.statLbl}>Referred</div>
                </div>
                <div style={s.stat}>
                  <div style={s.statVal}>{formatUSDC(referrals?.totalReward ?? '0')}</div>
                  <div style={s.statLbl}>Earned</div>
                </div>
              </div>
            </div>
          )}

          <div style={s.card}>
            <span style={s.label}>Tournament History</span>
            {data.tournaments.length === 0
              ? <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No tournaments yet.</div>
              : data.tournaments.map(t => (
                <div key={t.tournament_id} style={{ ...s.row, flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>#{t.tournament_id}</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{new Date(t.window_end * 1000).toLocaleDateString()}</span>
                  <span style={{ background: 'linear-gradient(90deg,#00D4FF,#8B00FF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 700 }}>{formatUSDC(t.prize_pool)}</span>
                  <span style={{ color: t.won ? '#00D4FF' : 'var(--text-secondary)', fontWeight: t.won ? 700 : 400 }}>{t.won ? '🏆 Won' : 'No win'}</span>
                  {t.won && (
                    <button onClick={() => setShareWin({ prizePool: t.prize_pool, tournamentId: t.tournament_id })}
                      style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', border: '1px solid #00D4FF44', borderRadius: 6, background: 'transparent', color: '#00D4FF', cursor: 'pointer' }}>
                      Share 𝕏
                    </button>
                  )}
                </div>
              ))
            }
          </div>

          <button style={{ ...s.btn, width: 'auto', padding: '8px 16px', fontSize: 12 }}
            onClick={() => { localStorage.removeItem('apexPlayerId'); setApexId(''); setData(null); setInput(''); }}>
            Unlink Account
          </button>
        </>
      )}

      {loading && <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 40 }}>Loading...</div>}
      {error   && <div style={{ color: '#FF006E', background: '#ff006e11', border: '1px solid #ff006e33', borderRadius: 8, padding: '12px 16px', fontSize: 14 }}>{error}</div>}

      {/* ── Fortnite section ──────────────────────────────────────────── */}
      <div style={{ marginTop: 48 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#FFDE00', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-block', width: 3, height: 14, background: '#FFDE00', borderRadius: 2 }} />
          Fortnite Profile
        </div>

        {!epicId && (
          <div style={{ ...s.card, border: '1px solid #FFDE0022', boxShadow: '0 0 15px #FFDE0018' }}>
            <span style={s.label}>Link your Fortnite username</span>
            <form onSubmit={handleFnRegister}>
              <input style={{ ...s.input, borderColor: '#FFDE0044' }} placeholder="Fortnite username (your in-game name)" value={epicInput} onChange={e => setEpicInput(e.target.value)} />
              {fnRegError && <div style={s.error}>{fnRegError}</div>}
              {fnRegOk    && <div style={s.success}>Fortnite account linked!</div>}
              <button style={{ ...s.btn, background: fnGrad }} type="submit">Link Fortnite Account</button>
            </form>
            {!walletAddress && <div style={{ ...s.error, marginTop: 8 }}>Connect your wallet first.</div>}
          </div>
        )}

        {fnLoading && <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 24 }}>Loading Fortnite profile...</div>}
        {fnError   && !fnLoading && <div style={{ color: '#FF006E', fontSize: 13 }}>{fnError}</div>}

        {epicId && !fnLoading && fnData && (
          <>
            <div style={{ ...s.card, border: '1px solid #FFDE0022', boxShadow: '0 0 15px #FFDE0018' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{fnData.user.epic_player_id}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{walletAddress || fnData.user.wallet_address}</div>
              <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                {[
                  { val: fnData.tournaments.length,                                 lbl: 'Played'   },
                  { val: fnData.tournaments.filter(t => t.won).length,              lbl: 'Won'       },
                  { val: fnData.tournaments.length ? `${Math.round((fnData.tournaments.filter(t => t.won).length / fnData.tournaments.length) * 100)}%` : '0%', lbl: 'Win Rate' },
                ].map(({ val, lbl }) => (
                  <div key={lbl} style={s.stat}>
                    <div style={{ ...s.statVal, background: fnGrad, backgroundSize: '400% 400%' }}>{val}</div>
                    <div style={s.statLbl}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...s.card, border: '1px solid #FFDE0022', boxShadow: '0 0 15px #FFDE0018' }}>
              <span style={s.label}>Fortnite Stats <span style={{ color: '#FFDE0055', fontWeight: 400 }}>· via fortnite-api.com</span></span>
              {!fnStats && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Stats unavailable — make sure your Epic stats are public.</div>}
              {fnStats && (
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  {[
                    { val: fnStats.kd?.toFixed(2) ?? '—',              lbl: 'K/D'         },
                    { val: fnStats.kills?.toLocaleString() ?? '—',      lbl: 'Total Kills'  },
                    { val: fnStats.wins?.toLocaleString()  ?? '—',      lbl: 'Wins'         },
                    { val: fnStats.winRate?.toFixed(1) + '%' ?? '—',    lbl: 'Win Rate'     },
                    { val: fnStats.killsPerMatch?.toFixed(1) ?? '—',    lbl: 'Kills/Match'  },
                  ].map(({ val, lbl }) => (
                    <div key={lbl} style={s.stat}>
                      <div style={{ ...s.statVal, background: fnGrad, backgroundSize: '400% 400%' }}>{val}</div>
                      <div style={s.statLbl}>{lbl}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {fnData.tournaments.length > 0 && (
              <div style={{ ...s.card, border: '1px solid #FFDE0022', boxShadow: '0 0 15px #FFDE0018' }}>
                <span style={s.label}>Fortnite Tournament History</span>
                {fnData.tournaments.map(t => (
                  <div key={t.tournament_id} style={{ ...s.row, flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>#{t.tournament_id}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{new Date(t.window_end * 1000).toLocaleDateString()}</span>
                    <span style={{ background: fnGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 700 }}>{formatUSDC(t.prize_pool)}</span>
                    <span style={{ color: t.won ? '#FFDE00' : 'var(--text-secondary)', fontWeight: t.won ? 700 : 400 }}>{t.won ? '🏆 Won' : 'No win'}</span>
                  </div>
                ))}
              </div>
            )}

            <button style={{ ...s.btn, width: 'auto', padding: '8px 16px', fontSize: 12, background: 'transparent', border: '1px solid #FFDE0033', color: '#FFDE00' }}
              onClick={() => { localStorage.removeItem('epicPlayerId'); setEpicId(''); setFnData(null); setEpicInput(''); }}>
              Unlink Fortnite
            </button>
          </>
        )}
      </div>

      {/* ── Tarkov section ──────────────────────────────────────────── */}
      <div style={{ marginTop: 48 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#C0A060', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-block', width: 3, height: 14, background: '#C0A060', borderRadius: 2 }} />
          Tarkov Arena Profile
        </div>

        {!tarkovId && (
          <div style={{ ...s.card, border: '1px solid #C0A06022', boxShadow: '0 0 15px #C0A06018' }}>
            <span style={s.label}>Link your Tarkov Arena username</span>
            <form onSubmit={handleTarkovRegister}>
              <input style={{ ...s.input, borderColor: '#C0A06044' }} placeholder="Tarkov Arena username (your in-game name)" value={tarkovInput} onChange={e => setTarkovInput(e.target.value)} />
              {tarkovRegError && <div style={s.error}>{tarkovRegError}</div>}
              {tarkovRegOk    && <div style={s.success}>Tarkov account linked!</div>}
              <button style={{ ...s.btn, background: 'linear-gradient(270deg,#C0A060,#9B4DCA,#e8c878)', backgroundSize: '400% 400%', animation: 'gradientShift 4s ease infinite' }} type="submit">Link Tarkov Account</button>
            </form>
            {!walletAddress && <div style={{ ...s.error, marginTop: 8 }}>Connect your wallet first.</div>}
          </div>
        )}

        {tarkovLoading && <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 24 }}>Loading Tarkov profile...</div>}
        {tarkovError   && !tarkovLoading && <div style={{ color: '#FF006E', fontSize: 13 }}>{tarkovError}</div>}

        {tarkovId && !tarkovLoading && tarkovData && (
          <>
            <div style={{ ...s.card, border: '1px solid #C0A06022', boxShadow: '0 0 15px #C0A06018' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{tarkovData.user.tarkov_player_id}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{walletAddress || tarkovData.user.wallet_address}</div>
              <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                {[
                  { val: tarkovData.tournaments.length,                                 lbl: 'Played'   },
                  { val: tarkovData.tournaments.filter(t => t.won).length,              lbl: 'Won'       },
                  { val: tarkovData.tournaments.length ? `${Math.round((tarkovData.tournaments.filter(t => t.won).length / tarkovData.tournaments.length) * 100)}%` : '0%', lbl: 'Win Rate' },
                ].map(({ val, lbl }) => (
                  <div key={lbl} style={s.stat}>
                    <div style={{ ...s.statVal, background: 'linear-gradient(270deg,#C0A060,#9B4DCA)', backgroundSize: '400% 400%' }}>{val}</div>
                    <div style={s.statLbl}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            {tarkovStats && (
              <div style={{ ...s.card, border: '1px solid #C0A06022', boxShadow: '0 0 15px #C0A06018' }}>
                <span style={s.label}>Tarkov Arena Stats <span style={{ color: '#C0A06055', fontWeight: 400 }}>· Last Hero · via player.tarkov.dev</span></span>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  {[
                    { val: tarkovStats.kills?.toLocaleString() ?? '—',                               lbl: 'LH Kills'      },
                    { val: tarkovStats.deaths > 0 ? (tarkovStats.kills / tarkovStats.deaths).toFixed(2) : '—', lbl: 'K/D Ratio' },
                    { val: tarkovStats.experience?.toLocaleString() ?? '—',                          lbl: 'Total XP'     },
                  ].map(({ val, lbl }) => (
                    <div key={lbl} style={s.stat}>
                      <div style={{ ...s.statVal, background: 'linear-gradient(270deg,#C0A060,#9B4DCA)', backgroundSize: '400% 400%' }}>{val}</div>
                      <div style={s.statLbl}>{lbl}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tarkovData.tournaments.length > 0 && (
              <div style={{ ...s.card, border: '1px solid #C0A06022', boxShadow: '0 0 15px #C0A06018' }}>
                <span style={s.label}>Tarkov Tournament History</span>
                {tarkovData.tournaments.map(t => (
                  <div key={t.tournament_id} style={{ ...s.row, flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>#{t.tournament_id}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{new Date(t.window_end * 1000).toLocaleDateString()}</span>
                    <span style={{ background: 'linear-gradient(90deg,#C0A060,#9B4DCA)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 700 }}>{formatUSDC(t.prize_pool)}</span>
                    <span style={{ color: t.won ? '#e8c878' : 'var(--text-secondary)', fontWeight: t.won ? 700 : 400 }}>{t.won ? '🏆 Won' : 'No win'}</span>
                  </div>
                ))}
              </div>
            )}

            <button style={{ ...s.btn, width: 'auto', padding: '8px 16px', fontSize: 12, background: 'transparent', border: '1px solid #C0A06033', color: '#C0A060' }}
              onClick={() => { localStorage.removeItem('tarkovPlayerId'); setTarkovId(''); setTarkovData(null); setTarkovInput(''); }}>
              Unlink Tarkov
            </button>
          </>
        )}
      </div>

      {shareWin && (
        <WinShareCard
          playerName={apexId}
          prizePool={shareWin.prizePool}
          tournamentId={shareWin.tournamentId}
          onClose={() => setShareWin(null)}
        />
      )}
    </div>
  );
}
